import type { ContentObjectDetail, ContentObjectList, ContentObjectView, JournalNote } from 'models';
import { getUserName, isJournalNote } from 'models';
import { useTranslation } from 'react-i18next';

import {
  Avatar,
  BookmarkFillIcon,
  BookmarkLineIcon,
  Button,
  Col,
  Dropdown,
  type MenuProps,
  More2LineIcon,
  NavigationPill,
  Row,
  Text,
  Title,
} from 'ui';
import { useBookmarkObjectMutation, useUnbookmarkObjectMutation } from 'features/bookmark';
import { useContentObjectPill } from '../hooks';
import { ContentObjectAuthor } from './content-object-author';
import { HeaderRow } from './header-row';

export type PillType = {
  fallbackPhoto?: string;
  link: string;
  name: string;
  photoUrl?: string;
};

type P = {
  actions?: MenuProps['items'];
  item: ContentObjectDetail | ContentObjectList | JournalNote;
  subtitle?: Date | string;
  view: ContentObjectView;
};

const ContentObjectHeader = ({ actions, item, subtitle: subtitleOverride, view }: P) => {
  const { t } = useTranslation();
  const author = isJournalNote(item) ? undefined : item.author;
  const subtitle = subtitleOverride ?? item.publishAt;
  const userFullName = getUserName({ firstName: author?.firstName, lastName: author?.lastName });

  const { isPending: isBookmarking, mutate: bookmark } = useBookmarkObjectMutation();
  const { isPending: isUnbookmarking, mutate: unbookmark } = useUnbookmarkObjectMutation();
  const pill = useContentObjectPill({ isMemberOfProgram: false, item });

  const bookmarkProperties =
    view === 'bookmark' || isJournalNote(item)
      ? undefined
      : {
          isBookmarked: item.bookmarked,
          isLoading: isBookmarking || isUnbookmarking,
          onBookmarkClick() {
            item.bookmarked
              ? unbookmark({ contentObject: item, type: 'rails' })
              : bookmark({ contentObject: item, type: 'rails' });
          },
        };

  return (
    <div className="feed-header">
      <div className="feed-header__content">
        <HeaderRow
          aside={
            author ? (
              <ContentObjectAuthor author={author}>
                <Avatar photo={author.profilePhoto} size="default">
                  {userFullName}
                </Avatar>
              </ContentObjectAuthor>
            ) : null
          }
          fullWidth={!author}
        >
          <Row>
            {author ? (
              <Col>
                <ContentObjectAuthor author={author}>
                  <Title level={4}>{userFullName}</Title>
                </ContentObjectAuthor>
              </Col>
            ) : null}

            <Col span={24}>
              <Text type="secondary">
                {subtitle instanceof Date ? t('dateFormats.dateTime', { date: subtitle }) : subtitle}
              </Text>
            </Col>
          </Row>
        </HeaderRow>
        <div className="feed-header__actions">
          {bookmarkProperties ? (
            <Button
              aria-label={bookmarkProperties.isBookmarked ? 'Remove bookmark' : 'Bookmark'}
              icon={bookmarkProperties.isBookmarked ? <BookmarkFillIcon /> : <BookmarkLineIcon />}
              loading={bookmarkProperties.isLoading}
              onClick={() => {
                bookmarkProperties.onBookmarkClick();
              }}
              type="link-secondary"
            />
          ) : null}
          {actions && actions.length > 0 ? (
            <Dropdown
              menu={{
                items: actions,
              }}
              trigger={['click']}
            >
              <Button aria-label="More actions" icon={<More2LineIcon />} type="link-secondary" />
            </Dropdown>
          ) : null}
        </div>
      </div>

      {pill ? (
        <HeaderRow className="feed-header__content__pill" fullWidth={!author}>
          <NavigationPill
            fallbackPhoto={pill.fallbackPhoto}
            link={pill.link}
            name={pill.name}
            photoUrl={pill.photoUrl}
          />
        </HeaderRow>
      ) : null}
    </div>
  );
};

export default ContentObjectHeader;
